import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { quizApi } from '../services/quizApi';
import { useQuiz } from '../hooks/useQuiz';
import { QuestionCountModal } from '../components/quiz/QuestionCountModal';
import { SubjectCard } from '../components/ui/SubjectCard';

export const Subjects: React.FC = () => {
  const navigate = useNavigate();
  const [startingQuiz, setStartingQuiz] = useState<string | null>(null);
  const [selectedSubject, setSelectedSubject] = useState<{ id: string; name: string; totalQuestions: number } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingSubject, setLoadingSubject] = useState<string | null>(null);
  const { startQuiz } = useQuiz();
  
  console.log('🏠 [DEBUG] Subjects component renderizzato con stati:', { 
    startingQuiz, 
    selectedSubject, 
    isModalOpen, 
    loadingSubject 
  });
  
  const { data: subjects, isLoading } = useQuery({
    queryKey: ['subjects'],
    queryFn: api.getSubjects,
  });

  console.log('📚 [DEBUG] Subjects data:', { subjects, isLoading });


  // Funzione per aprire modal selezione domande
  const handleSubjectClick = async (subject: { id: string; name: string; totalQuestions: number }) => {
    try {
      setLoadingSubject(subject.id);
      console.log('🖱️ Click su materia:', subject.name);
      
      // Ottieni il numero reale di domande dal backend
      const questionCount = await quizApi.getQuestionCount(subject.id);
      console.log('📊 Domande disponibili:', questionCount);
      
      setSelectedSubject({
        ...subject,
        totalQuestions: questionCount.activeQuestions
      });
      console.log('🎯 [DEBUG] Aprendo modal con subject:', subject.name, 'domande:', questionCount.activeQuestions);
      setIsModalOpen(true);
    } catch (error) {
      console.error('❌ Errore caricamento conteggio domande:', error);
      // Fallback al numero dal subject
      setSelectedSubject(subject);
      setIsModalOpen(true);
    } finally {
      setLoadingSubject(null);
    }
  };

  // Funzione per avviare il quiz locale con numero domande selezionato
  const handleStartQuiz = async (questionCount: number) => {
    if (!selectedSubject) return;

    try {
      console.log('🚀 [DEBUG] Avvio quiz per materia:', selectedSubject.name, 'domande:', questionCount);
      setStartingQuiz(selectedSubject.id);
      
      // Avvia quiz creando una sessione reale nel backend
      const session = await startQuiz(selectedSubject.id, questionCount);
      console.log('✅ [DEBUG] Quiz avviato con successo, session:', session);
      
      // Naviga alla pagina quiz con sessionId reale
      console.log('🧭 [DEBUG] Navigazione a:', `/quiz/${session.id}`);
      navigate(`/quiz/${session.id}`);
    } catch (error: any) {
      console.error('❌ Errore avvio quiz:', error);
      console.error('❌ Dettagli errore:', error.response?.data || error.message);
      // TODO: Mostrare errore all'utente
    } finally {
      console.log('🔄 [DEBUG] Reset startingQuiz state');
      setStartingQuiz(null);
    }
  };

  // Funzione per chiudere modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSubject(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento materie...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Grid delle materie */}
      <div className="grid grid-cols-2 gap-4">
        {subjects?.map((subject, index) => (
          <SubjectCard
            key={subject.id}
            subject={subject}
            index={index}
            onClick={handleSubjectClick}
          />
        ))}

        {subjects?.length === 0 && (
          <div className="col-span-2 text-center py-8 sm:py-12">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-300 rounded-lg"></div>
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nessuna materia disponibile</h3>
            <p className="mt-1 text-xs sm:text-sm text-gray-500">
              Contatta l'amministratore per maggiori informazioni.
            </p>
          </div>
        )}
      </div>

      {/* Modal selezione numero domande */}
      {selectedSubject && (
        <QuestionCountModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSelectCount={handleStartQuiz}
          subjectName={selectedSubject.name}
          totalQuestionsAvailable={selectedSubject.totalQuestions}
        />
      )}
    </div>
  );
};
